import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum PointsTransactionType {
  REDEMPTION = "REDEMPTION",
  LABEL_PURCHASED = "LABEL_PURCHASED",
  LABEL_VOIDED = "LABEL_VOIDED",
  REFUND = "REFUND", // This happens when we have to refund a balance transaction that earned points
}

export enum RedemptionMethod {
  OUTBOUND_LABELS_BALANCE_CREDIT = "OUTBOUND_LABELS_BALANCE_CREDIT",
}

export const RedemptionMethodToLabel: Record<RedemptionMethod, string> = {
  [RedemptionMethod.OUTBOUND_LABELS_BALANCE_CREDIT]: "Balance credit",
} as const;

export const PointsSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
  team: z.string().meta({ privacy: privacy.nonPii }),
  currentBalance: z.number().int().meta({ privacy: privacy.nonPii }),
  createdAt: z.date().meta({ privacy: privacy.nonPii }),
  updatedAt: z.date().meta({ privacy: privacy.nonPii }),
});

export type Points = z.infer<typeof PointsSchema>;

const BasePointsTransactionSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
  team: zExt.objectId().meta({ privacy: privacy.nonPii }),
  transactionTime: z.date().meta({ privacy: privacy.nonPii }),
  pointsChange: z.number().int().meta({ privacy: privacy.nonPii }),
});

export const LabelPurchasedPointsTransactionSchema =
  BasePointsTransactionSchema.extend({
    transactionType: z
      .literal(PointsTransactionType.LABEL_PURCHASED)
      .meta({ privacy: privacy.nonPii }),
    fulfillmentGroupId: zExt.objectId().meta({ privacy: privacy.nonPii }),
  });

export type LabelPurchasedPointsTransaction = z.infer<
  typeof LabelPurchasedPointsTransactionSchema
>;

const RedemptionPointsTransactionSchema = BasePointsTransactionSchema.extend({
  transactionType: z
    .literal(PointsTransactionType.REDEMPTION)
    .meta({ privacy: privacy.nonPii }),
  redemptionMethod: createEnum(RedemptionMethod, {
    enumName: "RedemptionMethod",
  }).meta({ privacy: privacy.nonPii }),
});

const LabelVoidedPointsTransactionSchema = BasePointsTransactionSchema.extend({
  transactionType: z
    .literal(PointsTransactionType.LABEL_VOIDED)
    .meta({ privacy: privacy.nonPii }),
  fulfillmentGroupId: zExt.objectId().meta({ privacy: privacy.nonPii }),
  labelPurchasedPointsTransactionId: zExt
    .objectId()
    .meta({ privacy: privacy.nonPii }),
});

const RefundPointsTransactionSchema = BasePointsTransactionSchema.extend({
  transactionType: z
    .literal(PointsTransactionType.REFUND)
    .meta({ privacy: privacy.nonPii }),
  fulfillmentGroupId: zExt.objectId().meta({ privacy: privacy.nonPii }),
  balanceTransactionRefundFor: zExt
    .objectId()
    .meta({ privacy: privacy.nonPii }),
});

export const PointsTransactionSchema = z.discriminatedUnion("transactionType", [
  LabelPurchasedPointsTransactionSchema,
  RedemptionPointsTransactionSchema,
  LabelVoidedPointsTransactionSchema,
  RefundPointsTransactionSchema,
]);

export type PointsTransaction = z.infer<typeof PointsTransactionSchema>;
